import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LOGO_URL } from '../../data/content';
import { scrollToSection } from '../../lib/scroll';

const NAV_LINKS = [
  { label: 'Produtos', target: '#produtos', testid: 'nav-link-produtos' },
  { label: 'Serviços', target: '#servicos', testid: 'nav-link-servicos' },
  { label: 'Sobre', target: '#sobre', testid: 'nav-link-sobre' },
  { label: 'Contato', target: '#contato-form', testid: 'nav-link-contato' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (target) => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection(target), 450);
      return;
    }
    scrollToSection(target);
  };

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== '/') navigate('/');
    else window.__lenis?.scrollTo(0) || window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#040D1A]/85 backdrop-blur-xl border-b border-amber-400/10 py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <button data-testid="nav-logo-button" onClick={goHome} className="flex items-center gap-3 text-left">
          <img
            src={LOGO_URL}
            alt="Logomarca Leão de Fogo"
            className="w-12 h-12 object-contain drop-shadow-[0_0_14px_rgba(252,211,77,0.35)]"
          />
          <span>
            <span className="block font-chapaza italic font-bold uppercase text-xl leading-none text-white tracking-wide">
              Leão de Fogo
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300/80 mt-1">
              Indústria · Comércio · Projetos
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.target}
              data-testid={link.testid}
              onClick={() => go(link.target)}
              className="group relative font-mono text-xs uppercase tracking-[0.22em] text-slate-200 hover:text-amber-300 transition-colors"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-amber-400 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-button"
          aria-label="Abrir menu"
          className="md:hidden text-white p-2"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="md:hidden overflow-hidden bg-[#040D1A]/95 backdrop-blur-xl border-b border-amber-400/10"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.target}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => go(link.target)}
                  className="text-left font-mono text-sm uppercase tracking-[0.22em] text-slate-200 hover:text-amber-300 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
